import { useTranslation } from "react-i18next";
import { Calendar, CalendarBlank, List } from "@phosphor-icons/react";

export type PlanViewMode = "week" | "month" | "list";

/**
 * 3-segment toggle between Plan view modes.
 *
 * - Week:  default 7-day grid
 * - Month: 5×7 condensed overview
 * - List:  flat chronological
 *
 * State lives in the page (URL ?view= param recommended for shareable
 * links). This atom only renders + emits onChange.
 */
export function PlanViewToggle({
  value,
  onChange,
  className = "",
}: {
  value: PlanViewMode;
  onChange: (mode: PlanViewMode) => void;
  className?: string;
}) {
  const { t } = useTranslation("plan");
  const items: { mode: PlanViewMode; label: string; Icon: typeof Calendar }[] = [
    { mode: "week", label: t("view_toggle.week", { defaultValue: "Неделя" }), Icon: Calendar },
    { mode: "month", label: t("view_toggle.month", { defaultValue: "Месяц" }), Icon: CalendarBlank },
    { mode: "list", label: t("view_toggle.list", { defaultValue: "Список" }), Icon: List },
  ];
  return (
    <div
      role="tablist"
      aria-label={t("view_toggle.aria", { defaultValue: "Режим отображения плана" })}
      data-testid="plan-view-toggle"
      className={`inline-flex rounded-pill border border-bg-border bg-bg-surface p-0.5 text-xs ${className}`}
    >
      {items.map(({ mode, label, Icon }) => {
        const active = value === mode;
        return (
          <button
            key={mode}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`plan-view-${mode}`}
            onClick={() => onChange(mode)}
            className={`px-3 py-1.5 rounded-pill inline-flex items-center gap-1.5 transition-colors ${
              active
                ? "bg-accent text-accent-on font-semibold"
                : "text-fg-secondary hover:text-fg-primary"
            }`}
          >
            <Icon size={12} aria-hidden />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}
